import { ref } from "vue";
import { differenceInCalendarDays, parse } from "date-fns";
import { orderInfoService } from "@/services/orderInfoService";
import { formatDateISO } from "@/utils";
import { buildStatusNotesPayload } from "@/utils/statusNotes";
import { lockHolderName } from "@/utils/lockDisplay";
import { createLockPollHalt } from "@/utils/lockPoll";
import { getPARoute } from "@/utils/paRoutes";
import { EXPIRY_WARNING_DAYS, resolvePaDisplayStatus } from "@/models/paStatus";

export function lockFields(lock) {
  return {
    isLocked: !!lock,
    lockedBy: lockHolderName(lock),
    lockedByUserId: lock?.userPk || null,
  };
}

function buildStatusNotes(item) {
  return buildStatusNotesPayload(item.statusNotes, item.orderSubmission?.operationalNotes);
}

export function normalizePARow(item) {
  const statusNotes = buildStatusNotes(item);
  return {
    id: item.orderKey,
    patientName: `${item.patientFirstName || ""} ${item.patientLastName || ""}`.trim() || "Unknown Patient",
    status: item.status,
    displayStatus: resolvePaDisplayStatus(
      item.status,
      item.orderSubmission?.pipelineStep,
      item.orderSubmission?.submissionType,
      { sentAt: item.appealSentAt, statusChangedAt: item.statusModifiedOn || item.createdOn }
    ),
    medication: item.medication || "",
    insurance: item.insuranceProvider || "",
    provider: `${item.providerFirstName || ""} ${item.providerLastName || ""}`.trim() || "",
    providerPhone: item.providerContactPhone || "",
    planType: item.insurancePlanType || "",
    dateCreated: item.createdOn,
    dateModified: item.modifiedOn,
    timeInStage: item.statusModifiedOn || item.createdOn,
    expiresOn: item.orderSubmission?.priorAuthExpiration,
    expirationWarning: getExpirationWarning(item.orderSubmission?.priorAuthExpiration),
    priorAuthNumber: item.orderSubmission?.priorAuthNumber || "",
    prescriptionNumber: item.prescriptionNumber || "",
    mrn: item.patientExternalId || "",
    lamarKey: item.orderKey || "",
    externalKey: item.orderSubmission?.portalSubmissionId || "",
    submissionType: item.orderSubmission?.submissionType || "",
    modifiedBy: item.modifiedByName || "",
    statusTags: item.statusTags || [],
    substatusTags: item.substatusTags || [],
    tagDueDates: item.tagDueDates || [],
    assignees: item.assignees || [],
    assignedGroups: item.assignedGroups || [],
    formStep: item.formStep,
    canUploadDecision: item.canUploadDecision,
    staffEditOverride: item.staffEditOverride,
    archivedReason: item.archivedReason,
    isArchived: item.status === "Archived",
    ...lockFields(item.lock),
    statusNotes,
    hasNotes: !!statusNotes,
    responseDocuments: item.orderSubmission?.responseDocuments || [],
    responseDocumentUrl: item.orderSubmission?.responseDocumentUrl || null,
    hasResponseDocs: !!item.orderSubmission?.responseDocumentUrl || item.orderSubmission?.responseDocuments?.length > 0,
    navigateTo: getPARoute(item.orderKey, item.status),
    raw: item,
  };
}

export function normalizePARows(items) {
  return items.map(normalizePARow);
}

function getExpirationWarning(expirationDate) {
  if (!expirationDate) return null;
  const expDate = parse(expirationDate.substring(0, 10), "yyyy-MM-dd", new Date());
  const daysLeft = differenceInCalendarDays(expDate, new Date());
  if (daysLeft < 0) return { level: "expired", label: "Expired" };
  if (daysLeft === 0) return { level: "warning", label: "Expires today" };
  if (daysLeft <= EXPIRY_WARNING_DAYS) return { level: "warning", label: `Expires in ${daysLeft}d` };
  return null;
}

export function usePATable() {
  const rows = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const lockPollError = ref(null);
  const totalCount = ref(0);
  const currentPage = ref(1);
  const statusCounts = ref([]);
  const modifiers = ref([]);
  let fetchVersion = 0;
  let lockPollInFlight = false;
  const lockPoll = createLockPollHalt(lockPollError);

  async function fetchData(filters, page = 1, pageSize, { silent = false } = {}) {
    const thisVersion = ++fetchVersion;
    if (!silent) loading.value = true;
    error.value = null;
    try {
      const dateFrom = formatDateISO(filters.dateFrom);
      const dateTo = formatDateISO(filters.dateTo);
      const tags = filters.tags || undefined;
      const tagsMatch = tags && filters.tagsMatch === "any" ? "any" : undefined;
      const dueFrom = tags ? formatDateISO(filters.dueFrom) : undefined;
      const dueTo = tags ? formatDateISO(filters.dueTo) : undefined;
      const modifiedBy = filters.modifiedBy || undefined;
      const assignedUser = filters.assignedUser || undefined;
      const assignedGroup = filters.assignedGroup || undefined;
      const substatus = filters.substatus || undefined;
      const substatusMatch = substatus && filters.substatusMatch === "any" ? "any" : undefined;
      const response = filters.search
        ? await orderInfoService.searchOrders(
            {
              searchTerm: filters.search,
              status: filters.status,
              orderBy: filters.orderBy,
              date_from: dateFrom,
              date_to: dateTo,
              tags,
              tags_match: tagsMatch,
              due_from: dueFrom,
              due_to: dueTo,
              modified_by: modifiedBy,
              assigned_user: assignedUser,
              assigned_group: assignedGroup,
              substatus,
              substatus_match: substatusMatch,
            },
            page,
            pageSize
          )
        : await orderInfoService.getListOfOrders(
            page,
            filters.status,
            "",
            undefined,
            dateFrom,
            dateTo,
            filters.orderBy,
            tags,
            pageSize,
            modifiedBy,
            assignedUser,
            tagsMatch,
            dueFrom,
            dueTo,
            {
              ...(assignedGroup ? { assigned_group: assignedGroup } : {}),
              ...(substatus ? { substatus, substatus_match: substatusMatch } : {}),
            }
          );

      if (thisVersion !== fetchVersion) return;
      rows.value = normalizePARows(response.results || []);
      totalCount.value = response.count || 0;
      modifiers.value = response.modifiers || [];
      currentPage.value = page;
    } catch (e) {
      if (thisVersion !== fetchVersion) return;
      error.value = "Failed to load submissions. Please try again.";
    } finally {
      if (thisVersion === fetchVersion) loading.value = false;
    }
  }

  async function fetchStatusCounts(filters = {}) {
    try {
      statusCounts.value = await orderInfoService.getOrderStatusCounts(filters);
    } catch {
      // Silent fail — counts are non-critical
    }
  }

  /**
   * Refresh the lock badge on every loaded row from server truth: a row absent from the reply is
   * unlocked. Reads `fetchVersion` without bumping it — a list fetch begun since the request went
   * out discards this reply, and rows never asked about are left alone.
   */
  async function fetchLocks() {
    if (lockPollInFlight || lockPoll.isHalted()) return;
    const orderKeys = rows.value.map((row) => row.id);
    if (!orderKeys.length) return;
    const thisVersion = fetchVersion;
    lockPollInFlight = true;
    try {
      const response = await orderInfoService.getSubmissionLocks(orderKeys);
      if (thisVersion !== fetchVersion) return;
      if (!Array.isArray(response?.locks)) {
        lockPoll.halt();
        return;
      }
      const asked = new Set(orderKeys);
      const heldByKey = new Map(response.locks.map((lock) => [lock.orderKey, lock]));
      for (const row of rows.value) {
        if (asked.has(row.id)) Object.assign(row, lockFields(heldByKey.get(row.id)));
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status >= 400 && status < 500) lockPoll.halt();
    } finally {
      lockPollInFlight = false;
    }
  }

  return {
    rows,
    loading,
    error,
    lockPollError,
    totalCount,
    currentPage,
    statusCounts,
    modifiers,
    fetchData,
    fetchStatusCounts,
    fetchLocks,
  };
}
